import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from '../components/ui/dialog';
import { 
  Plus, Search, User, Mail, Phone, Briefcase, 
  Clock, FileText
} from 'lucide-react';

const sourceOptions = [
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'naukri', label: 'Naukri' },
  { value: 'indeed', label: 'Indeed' },
  { value: 'referral', label: 'Employee Referral' },
  { value: 'vendor', label: 'Vendor/Agency' },
  { value: 'career_page', label: 'Career Page' },
  { value: 'campus', label: 'Campus Hiring' },
  { value: 'other', label: 'Other' }
];

const tagColors = {
  hot: 'bg-red-100 text-red-800',
  warm: 'bg-yellow-100 text-yellow-800',
  cold: 'bg-blue-100 text-blue-800'
};

export default function CandidatesPage() {
  const navigate = useNavigate();
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [filters, setFilters] = useState({
    source: '',
    tags: '',
    search: ''
  });
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const [newCandidate, setNewCandidate] = useState({
    name: '',
    email: '',
    phone: '',
    years_experience: '',
    current_company: '',
    current_designation: '',
    current_ctc: '',
    expected_ctc: '',
    notice_period: '',
    skills: '',
    source: 'linkedin',
    source_details: '',
    tags: '',
    notes: ''
  });
  const [resumeFile, setResumeFile] = useState(null);

  const fetchCandidates = async () => {
    try {
      setLoading(true);
      const params = { page, limit: 20 };
      if (filters.source) params.source = filters.source;
      if (filters.tags) params.tags = filters.tags;
      if (filters.search) params.search = filters.search;

      const response = await api.get('/recruitment/candidates', { params });
      setCandidates(response.data.candidates);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Error fetching candidates:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCandidates();
  }, [page, filters]);

  const handleAddCandidate = async () => {
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('name', newCandidate.name);
      formData.append('email', newCandidate.email);
      formData.append('phone', newCandidate.phone || '');
      formData.append('years_experience', newCandidate.years_experience || '0');
      formData.append('current_company', newCandidate.current_company || '');
      formData.append('current_designation', newCandidate.current_designation || '');
      formData.append('current_ctc', newCandidate.current_ctc || '');
      formData.append('expected_ctc', newCandidate.expected_ctc || '');
      formData.append('notice_period', newCandidate.notice_period || '');
      formData.append('skills', newCandidate.skills || '');
      formData.append('source', newCandidate.source);
      formData.append('source_details', newCandidate.source_details || '');
      formData.append('tags', newCandidate.tags || '');
      formData.append('notes', newCandidate.notes || '');
      
      if (resumeFile) {
        formData.append('resume', resumeFile);
      }

      await api.post('/recruitment/candidates', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setShowAddDialog(false);
      setNewCandidate({
        name: '', email: '', phone: '', years_experience: '', current_company: '',
        current_designation: '', current_ctc: '', expected_ctc: '', notice_period: '',
        skills: '', source: 'linkedin', source_details: '', tags: '', notes: ''
      });
      setResumeFile(null);
      fetchCandidates();
    } catch (error) {
      console.error('Error adding candidate:', error);
      alert(error.response?.data?.detail || 'Failed to add candidate');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Candidate Database</h1>
          <p className="text-gray-500">Manage and track all candidates</p>
        </div>
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="w-4 h-4 mr-2" />
              Add Candidate
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Add New Candidate</DialogTitle>
              <DialogDescription>Enter candidate details and upload resume</DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-4">
              <div className="col-span-2">
                <Label>Full Name *</Label>
                <Input
                  value={newCandidate.name}
                  onChange={(e) => setNewCandidate(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="John Doe"
                />
              </div>
              <div>
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={newCandidate.email}
                  onChange={(e) => setNewCandidate(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="john@example.com"
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input
                  value={newCandidate.phone}
                  onChange={(e) => setNewCandidate(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="+91 98765 43210"
                />
              </div>
              <div>
                <Label>Years of Experience</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.5"
                  value={newCandidate.years_experience}
                  onChange={(e) => setNewCandidate(prev => ({ ...prev, years_experience: e.target.value }))}
                  placeholder="5"
                />
              </div>
              <div>
                <Label>Notice Period (days)</Label>
                <Input
                  type="number"
                  min="0"
                  value={newCandidate.notice_period}
                  onChange={(e) => setNewCandidate(prev => ({ ...prev, notice_period: e.target.value }))}
                  placeholder="30"
                />
              </div>
              <div>
                <Label>Current Company</Label>
                <Input
                  value={newCandidate.current_company}
                  onChange={(e) => setNewCandidate(prev => ({ ...prev, current_company: e.target.value }))}
                  placeholder="Acme Inc."
                />
              </div>
              <div>
                <Label>Current Designation</Label>
                <Input
                  value={newCandidate.current_designation}
                  onChange={(e) => setNewCandidate(prev => ({ ...prev, current_designation: e.target.value }))}
                  placeholder="Senior Developer"
                />
              </div>
              <div>
                <Label>Current CTC (LPA)</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.5"
                  value={newCandidate.current_ctc}
                  onChange={(e) => setNewCandidate(prev => ({ ...prev, current_ctc: e.target.value }))}
                  placeholder="15"
                />
              </div>
              <div>
                <Label>Expected CTC (LPA)</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.5"
                  value={newCandidate.expected_ctc}
                  onChange={(e) => setNewCandidate(prev => ({ ...prev, expected_ctc: e.target.value }))}
                  placeholder="20"
                />
              </div>
              <div>
                <Label>Source</Label>
                <Select
                  value={newCandidate.source}
                  onValueChange={(value) => setNewCandidate(prev => ({ ...prev, source: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {sourceOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Source Details (Referrer/Agency name)</Label>
                <Input
                  value={newCandidate.source_details}
                  onChange={(e) => setNewCandidate(prev => ({ ...prev, source_details: e.target.value }))}
                  placeholder="Referred by John"
                />
              </div>
              <div className="col-span-2">
                <Label>Skills (comma-separated)</Label>
                <Input
                  value={newCandidate.skills}
                  onChange={(e) => setNewCandidate(prev => ({ ...prev, skills: e.target.value }))}
                  placeholder="React, Node.js, Python, AWS"
                />
              </div>
              <div>
                <Label>Tags (hot, warm, cold)</Label>
                <Input
                  value={newCandidate.tags}
                  onChange={(e) => setNewCandidate(prev => ({ ...prev, tags: e.target.value }))}
                  placeholder="hot"
                />
              </div>
              <div>
                <Label>Resume</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={(e) => setResumeFile(e.target.files[0])}
                  />
                </div>
                {resumeFile && (
                  <p className="text-sm text-gray-500 mt-1">
                    Selected: {resumeFile.name}
                  </p>
                )}
              </div>
              <div className="col-span-2">
                <Label>Notes</Label>
                <Textarea
                  value={newCandidate.notes}
                  onChange={(e) => setNewCandidate(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Additional notes about the candidate..."
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
              <Button onClick={handleAddCandidate} disabled={submitting || !newCandidate.name || !newCandidate.email}>
                {submitting ? 'Adding...' : 'Add Candidate'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Search by name, email, company, skills..."
                  value={filters.search}
                  onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                  className="pl-10"
                />
              </div>
            </div>
            <Select
              value={filters.source}
              onValueChange={(value) => setFilters(prev => ({ ...prev, source: value === 'all' ? '' : value }))}
            >
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Source" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sources</SelectItem>
                {sourceOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={filters.tags}
              onValueChange={(value) => setFilters(prev => ({ ...prev, tags: value === 'all' ? '' : value }))}
            >
              <SelectTrigger className="w-[120px]">
                <SelectValue placeholder="Tag" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Tags</SelectItem>
                <SelectItem value="hot">Hot</SelectItem>
                <SelectItem value="warm">Warm</SelectItem>
                <SelectItem value="cold">Cold</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Candidates List */}
      <Card>
        <CardHeader>
          <CardTitle>Candidates ({total})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">Loading...</div>
          ) : candidates.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No candidates found. Add your first candidate to get started.
            </div>
          ) : (
            <div className="space-y-4">
              {candidates.map((candidate) => (
                <div
                  key={candidate.id}
                  className="border rounded-lg p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => navigate(`/recruitment/candidates/${candidate.id}`)}
                >
                  <div className="flex justify-between items-start">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                          <User className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold">{candidate.name}</h3>
                          <p className="text-sm text-gray-500">
                            {candidate.current_designation || 'N/A'} at {candidate.current_company || 'N/A'}
                          </p>
                        </div>
                        {candidate.tags?.map((tag, idx) => (
                          <Badge key={idx} className={tagColors[tag] || 'bg-gray-100'}>
                            {tag}
                          </Badge>
                        ))}
                      </div>
                      <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <Mail className="w-4 h-4" />
                          {candidate.email}
                        </span>
                        {candidate.phone && (
                          <span className="flex items-center gap-1">
                            <Phone className="w-4 h-4" />
                            {candidate.phone}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Briefcase className="w-4 h-4" />
                          {candidate.years_experience || 0} years
                        </span>
                        {candidate.notice_period && (
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {candidate.notice_period} days notice
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {candidate.skills?.slice(0, 6).map((skill, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs">
                            {skill}
                          </Badge>
                        ))}
                        {candidate.skills?.length > 6 && (
                          <Badge variant="outline" className="text-xs">
                            +{candidate.skills.length - 6} more
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div className="text-right space-y-2">
                      <div className="flex items-center gap-4">
                        <div className="text-center">
                          <p className="text-lg font-semibold text-green-600">
                            {candidate.current_ctc ? `₹${candidate.current_ctc}L` : 'N/A'}
                          </p>
                          <p className="text-xs text-gray-500">Current</p>
                        </div>
                        <div className="text-center">
                          <p className="text-lg font-semibold text-blue-600">
                            {candidate.expected_ctc ? `₹${candidate.expected_ctc}L` : 'N/A'}
                          </p>
                          <p className="text-xs text-gray-500">Expected</p>
                        </div>
                      </div>
                      <div className="flex items-center justify-end gap-2">
                        <Badge variant="outline">{candidate.source?.replace('_', ' ')}</Badge>
                        {candidate.active_applications > 0 && (
                          <Badge className="bg-blue-100 text-blue-800">
                            {candidate.active_applications} active
                          </Badge>
                        )}
                        {candidate.resume_url && (
                          <a 
                            href={candidate.resume_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Badge className="bg-purple-100 text-purple-800 cursor-pointer hover:bg-purple-200">
                              <FileText className="w-3 h-3 mr-1" />
                              Resume
                            </Badge>
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {/* Pagination */}
          {total > 20 && (
            <div className="flex justify-center gap-2 mt-4">
              <Button
                variant="outline"
                disabled={page === 1}
                onClick={() => setPage(p => p - 1)}
              >
                Previous
              </Button>
              <span className="flex items-center px-4">
                Page {page} of {Math.ceil(total / 20)}
              </span>
              <Button
                variant="outline"
                disabled={page * 20 >= total}
                onClick={() => setPage(p => p + 1)}
              >
                Next
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
